import { AppData, Router } from '@lightningjs/sdk';
import MovieDetailsPage from 'components/pages/movie-details/MovieDetailsPage';
import SeriesDetailsPage from 'components/pages/series-details/SeriesDetailsPage';
import SplashPage from 'components/pages/splash/SplashPage';
import EpgPage from 'components/pages/epg/EpgPage';
import SettingsPage from 'components/pages/settings/SettingsPage';
import ErrorPage from 'components/pages/error/ErrorPage';
import SearchPage from 'components/pages/search/SearchPage';
import ConvivaAnalytics from 'services/analytics/Conviva';
import {
  getShowByShowSlug,
  getEpisodeListByShowSlug,
  getExtrasListByShowSlug,
  getVideo,
  initializeCacheTimeouts,
  useCacheBackup,
  getEpgPage,
  getContentHub,
  getConfig,
  getEpgChannelBySlug,
  getAllLiveEvents,
  getLiveEvent,
} from 'services/cwService';
import ComscoreAnalytics from 'services/analytics/Comscore';
import { StaticViewContexts, ViewContext } from 'types/analytics';
import { loadImaDaiScript } from 'aliases';
import NielsenAnalytics from 'services/analytics/Nielsen';
import MParticleAnalytics from 'services/analytics/MParticle';
import { PageId } from 'types/pageId';
import {
  getMediaDataFromIdentifiers,
  getDetailsRecommended,
  getTrending,
  getEndCardRecommended,
} from 'services/tivoService';
import { cleanupEpgChannelData } from 'services/cwData';
import ContentHubPage from 'components/pages/content-hub/ContentHubPage';
import BackgroundingService from 'services/BackgroundingService';
import { constants } from 'aliases';
import {
  getPageIdFromHash,
  getContentWrapper,
  shouldShowLogin,
  navigateHome,
} from 'support/routerUtils';
import { ListItemContext, SelectItemContext } from 'types/events';
import { ErrorCause } from 'components/widgets/ErrorModal';
import CollectionVisibilityService from 'services/CollectionVisibilityService';
import { ContentHubSlugs } from 'types/contentHubs';
import LiveEventDetailsPage from 'components/pages/live-event-details/LiveEventDetailsPage';
import Request from '@lightningjs/sdk/src/Router/model/Request';
import BrightcoveAnalytics from 'services/analytics/Brightcove';
import {
  ErrorCode,
  reportError,
  reportPageView,
} from 'services/analytics/reportingServicePage';
import MyStuffPage, {
  CONTINUE_WATCHING_MAX_ITEMS,
  WATCHLIST_MAX_ITEMS,
  FAVORITE_CHANNELS_MAX_ITEMS,
} from 'components/pages/myStuff/MyStuffPage';
import LoginPage from 'components/pages/login/LoginPage';
import { isWatchlistItem } from 'support/personaUtils';
import {
  getFavoriteChannelLaneItems,
  getWatchlistLaneItems,
} from 'support/personaUtils';
import MyChannelsPage from 'components/pages/myStuff/MyChannelsPage';
import MyHistoryPage from 'components/pages/myStuff/MyHistoryPage';
import MyWatchlistPage from 'components/pages/myStuff/MyWatchlistPage';
import VodPlaybackPage from 'components/pages/playback/PlaybackPage/VodPlaybackPage';
import LivePlaybackPage from 'components/pages/playback/PlaybackPage/LivePlaybackPage';

export enum RoutePath {
  SPLASH = '$', // do not change this value, special path (see: https://lightningjs.io/docs/#/lightning-sdk-reference/plugins/router/configuration?id=bootpage)
  HOME_SCREEN = 'content-hub/home-screen',
  SEARCH = 'search',
  EPG = 'channels',
  SETTINGS = 'settings',
  MOVIE_DETAIL = 'movie-detail',
  SERIES_DETAIL = 'series-detail',
  LIVE_EVENT_DETAIL = 'live-event-detail',
  PLAY = 'play',
  CONTENT_HUB = 'content-hub',
  MY_STUFF = 'my-cw', // TODO: Handle NewsNation
  LOGIN = 'login',
  FAVORITE_CHANNELS = 'favorite-channels',
  CONTINUE_WATCHING = 'continue-watching',
  WATCHLIST = 'watchlist',
  ERROR = '!', // Special error path (Loads any time navigation hooks fail)
  NOT_FOUND = '*', // Lightning's Reserved path for NoFoundPage
}

export enum FromRoute {
  NAVBAR = 'NavBar',
}

export enum WidgetNames {
  ADVANCED_ERROR_MODAL = 'AdvancedErrorModal',
  APP_LAUNCH = 'AppLaunch',
  AYSW_MODAL = 'AyswModal',
  BRAZE_NOTIFICATION = 'BrazeNotification',
  CREATE_PASSWORD_MODAL = 'CreatePasswordModal',
  EMAIL_CONFIRMATION_MODAL = 'EmailConfirmationModal',
  FORGOT_PASSWORD_CONFIRMATION_MODAL = 'ForgotPasswordConfirmationModal',
  EMAIL_INPUT_MODAL = 'EmailInputModal',
  ENTER_PARENTAL_PIN = 'EnterParentalPin',
  ERROR_MODAL = 'ErrorModal',
  EXIT_MODAL = 'ExitModal',
  FORGOT_PASSWORD_MODAL = 'ForgotPasswordModal',
  LOGIN_LANDING = 'LoginLanding',
  MODAL = 'Modal',
  NAVBAR = 'NavBar',
  RESET_PARENTAL_PIN = 'ResetParentalPin',
  REMOVE_CONFIRMATION_MODAL = 'RemoveConfirmationModal',
  SET_PARENTAL_PIN = 'SetParentalPin',
  SIGN_UP_MODAL = 'SignUpModal',
  SIGN_IN_MODAL = 'SignInModal',
  SIGN_IN_CONFIRMATION_MODAL = 'SignInConfirmationModal',
  SIGN_IN_PASSWORD_MODAL = 'SignInPasswordModal',
  SIGN_OUT_MODAL = 'SignOutModal',
  TOP_NAV = 'TopNav',
}

export const loginWidgets: Array<WidgetNames> = [
  WidgetNames.CREATE_PASSWORD_MODAL,
  WidgetNames.EMAIL_INPUT_MODAL,
  WidgetNames.EMAIL_CONFIRMATION_MODAL,
  WidgetNames.FORGOT_PASSWORD_CONFIRMATION_MODAL,
  WidgetNames.FORGOT_PASSWORD_MODAL,
  WidgetNames.LOGIN_LANDING,
  WidgetNames.SIGN_UP_MODAL,
  WidgetNames.SIGN_IN_MODAL,
  WidgetNames.SIGN_IN_CONFIRMATION_MODAL,
  WidgetNames.SIGN_IN_PASSWORD_MODAL,
];

const defaultWidgets: Array<WidgetNames> = [
  WidgetNames.ADVANCED_ERROR_MODAL,
  WidgetNames.APP_LAUNCH,
  WidgetNames.ENTER_PARENTAL_PIN,
  WidgetNames.EXIT_MODAL,
  WidgetNames.RESET_PARENTAL_PIN,
  WidgetNames.SET_PARENTAL_PIN,
];

const pageWidgets: Array<WidgetNames> = [
  WidgetNames.TOP_NAV,
  WidgetNames.BRAZE_NOTIFICATION,
  ...defaultWidgets,
];

export const EnabledWidgets: Record<PageId, WidgetNames[]> = {
  [PageId.SPLASH]: [WidgetNames.ERROR_MODAL],
  [PageId.SEARCH]: [...pageWidgets, WidgetNames.NAVBAR],
  [PageId.EPG]: [
    ...defaultWidgets,
    WidgetNames.NAVBAR,
    WidgetNames.ERROR_MODAL,
    WidgetNames.MODAL,
    WidgetNames.AYSW_MODAL,
    WidgetNames.TOP_NAV,
    WidgetNames.BRAZE_NOTIFICATION,
  ],
  [PageId.HOME]: [...pageWidgets, WidgetNames.NAVBAR, WidgetNames.ERROR_MODAL],
  [PageId.CONTENT_HUB]: [
    ...pageWidgets,
    WidgetNames.NAVBAR,
    WidgetNames.ERROR_MODAL,
  ],
  [PageId.SETTINGS]: [...pageWidgets, WidgetNames.SIGN_OUT_MODAL],
  [PageId.MOVIE_DETAIL]: [...pageWidgets],
  [PageId.SERIES_DETAIL]: [...pageWidgets],
  [PageId.LIVE_EVENT_DETAIL]: [WidgetNames.APP_LAUNCH, WidgetNames.TOP_NAV],
  [PageId.PLAYBACK]: [
    ...defaultWidgets,
    WidgetNames.ERROR_MODAL,
    WidgetNames.MODAL,
    WidgetNames.AYSW_MODAL,
  ],
  [PageId.MY_STUFF]: [...pageWidgets, WidgetNames.NAVBAR],
  [PageId.FAVORITE_CHANNELS]: [
    ...pageWidgets,
    WidgetNames.REMOVE_CONFIRMATION_MODAL,
  ],
  [PageId.CONTINUE_WATCHING]: [
    ...pageWidgets,
    WidgetNames.REMOVE_CONFIRMATION_MODAL,
  ],
  [PageId.WATCHLIST]: [...pageWidgets, WidgetNames.REMOVE_CONFIRMATION_MODAL],
  [PageId.ERROR]: [WidgetNames.ERROR_MODAL],
  [PageId.CONTINUE]: [WidgetNames.ERROR_MODAL],
  [PageId.UNKNOWN]: [WidgetNames.ERROR_MODAL],
  [PageId.INITIAL_LANDING_PAGE]: [WidgetNames.LOGIN_LANDING],
  [PageId.LOGIN]: [
    ...loginWidgets,
    WidgetNames.APP_LAUNCH,
    WidgetNames.EXIT_MODAL,
    WidgetNames.BRAZE_NOTIFICATION,
  ],
} as const;

const routes = [
  {
    // on, before, or after functions don't work for the boot page
    path: RoutePath.SPLASH,
    widgets: EnabledWidgets[PageId.SPLASH],
    component: SplashPage,
    options: {
      preventStorage: true,
    },
  },
  {
    path: RoutePath.LOGIN,
    component: LoginPage,
    widgets: EnabledWidgets[PageId.LOGIN],
    options: { preventStorage: true },
  },
  {
    path: RoutePath.SEARCH,
    component: SearchPage,
    widgets: EnabledWidgets[PageId.SEARCH],
    on: async (
      page: SearchPage,
      routes: Router.PageParams & { query?: string },
    ) => {
      const explorePromise = getContentWrapper(() =>
        getContentHub(ContentHubSlugs.BROWSE),
      );
      const trendingPromise = getTrending().then(ids =>
        getMediaDataFromIdentifiers(
          AppData?.device.isLowMemoryModel() ? ids.slice(0, 8) : ids,
        ),
      );

      const [exploreData, trendingData] = await Promise.all([
        explorePromise,
        trendingPromise,
      ]);

      const { from, query } = routes;
      const reset = from === FromRoute.NAVBAR;

      page.pageData = { exploreData, trendingData, reset, query };
    },
  },
  {
    path: `${RoutePath.EPG}/:channel?`,
    component: EpgPage,
    widgets: EnabledWidgets[PageId.EPG],
    on: async (
      page: EpgPage,
      routes: { channel?: string; viewContext?: ViewContext },
    ) => {
      // Get first EPG page
      const pageSize = constants.epg.pageSize;
      const epgPageData = await getContentWrapper(() =>
        getEpgPage(1, pageSize),
      );
      if (!epgPageData) return;

      // Keep track of channel added to first EPG page
      const { channel: selectedChannelSlug } = routes;
      let addedChannelSlug: string | undefined;

      if (selectedChannelSlug) {
        // Check if selected channel is in the first page
        const selectedChannelIndex = epgPageData.channels.findIndex(
          channel => channel.slug === selectedChannelSlug,
        );

        if (selectedChannelIndex === -1) {
          // Selected channel is not available in the first page: Fetch it
          try {
            const epgChannel = await getEpgChannelBySlug(selectedChannelSlug);
            const channel = epgChannel.channels[0]!;
            epgPageData.channels.unshift(channel);

            addedChannelSlug = selectedChannelSlug;
          } catch (e) {
            // Ignore error, won't show selected channel if not found
            reportError({ error: e, userFacing: true, code: ErrorCode.c6 });
          }
        } else {
          // Selected channel is in the first page: Swap it with the first channel
          const selectedChannel = epgPageData.channels[selectedChannelIndex]!;
          epgPageData.channels[selectedChannelIndex] = epgPageData.channels[0]!;
          epgPageData.channels[0] = selectedChannel;
        }
      }

      // Note that we can provide the current date since the cleanup function
      // will convert it to a 30 minute interval for us
      const channels = cleanupEpgChannelData(epgPageData.channels, new Date());
      const maxChannels = epgPageData.maxChannels;

      let viewContext: ViewContext;
      if (selectedChannelSlug && routes.viewContext) {
        viewContext = routes.viewContext;
      } else if (selectedChannelSlug) {
        viewContext = StaticViewContexts.DEEPLINK;
      } else {
        viewContext = StaticViewContexts.EPG;
      }

      page.pageData = {
        channels,
        maxChannels,
        addedChannelSlug,
        viewContext,
      };
    },
  },
  {
    path: RoutePath.SETTINGS,
    component: SettingsPage,
    widgets: EnabledWidgets[PageId.SETTINGS],
  },
  {
    path: `${RoutePath.MOVIE_DETAIL}/:id`,
    component: MovieDetailsPage,
    widgets: EnabledWidgets[PageId.MOVIE_DETAIL],
    on: async (
      page: MovieDetailsPage,
      routes: {
        id: string;
        listItemContext: ListItemContext;
        fromPageId: PageId;
      },
    ) => {
      const slug = routes.id;
      const show = await getContentWrapper(() => getShowByShowSlug(slug));
      if (!show) return Promise.reject('Movie Details: Missing movie');

      const { listItemContext, fromPageId } = routes;
      const guid = show.firstEpisode?.guid ?? '';
      const isLoggedIn = !!AppData!.loginRadiusService?.isLoggedIn();

      const detailsPromise = getDetailsRecommended(guid);
      const historyPromise = AppData!.historyService.getContentBookmarks(slug);
      const myStuffPromise = isLoggedIn
        ? isWatchlistItem(show.id)
        : Promise.resolve(false);

      const [recommended, history, isMyStuffAdded] = await Promise.all([
        detailsPromise,
        historyPromise,
        myStuffPromise,
      ]);

      // Check if the movie we are on is in the recommended section and remove
      const duplicateIndex = recommended.findIndex(
        show => show.showSlug === slug || show.firstEpisode?.guid === guid,
      );
      if (duplicateIndex > -1) recommended.splice(duplicateIndex, 1);

      reportPageView(PageId.MOVIE_DETAIL, slug);
      const pageData = {
        show,
        recommended,
        listItemContext: listItemContext,
        fromPageId: fromPageId ?? PageId.UNKNOWN,
        isMyStuffAdded,
        history,
      };

      page.pageData = pageData;
    },
  },
  {
    path: `${RoutePath.SERIES_DETAIL}/:id/:season?`,
    component: SeriesDetailsPage,
    widgets: EnabledWidgets[PageId.SERIES_DETAIL],
    on: async (
      page: SeriesDetailsPage,
      routes: {
        id: string;
        season: string;
        listItemContext: ListItemContext;
        fromPageId: PageId;
      },
    ) => {
      const slug = routes.id;
      const show = await getContentWrapper(() => getShowByShowSlug(slug));
      if (!show) return Promise.reject('Series Detail: Missing series');

      const { listItemContext, fromPageId, season } = routes;
      const { seasons, id } = show;
      const isLoggedIn = AppData!.loginRadiusService?.isLoggedIn();

      const episodesListPromises = Promise.all(
        seasons.map(season => getEpisodeListByShowSlug(id, season)),
      ).then(episodes => {
        episodes.forEach(episode => {
          if (episode.season) {
            episode.season.episodeCount = episode.items.length;
          }
        });
        return episodes.filter(episode => episode.items.length > 0);
      });
      const extrasPromise = getContentWrapper(
        () => getExtrasListByShowSlug(id),
        false,
        false,
      );
      const historyPromise = AppData!.historyService
        .getContentBookmarks(slug)
        .then(result => {
          return result ?? {};
        });
      const myStuffPromise = isLoggedIn
        ? isWatchlistItem(show.id)
        : Promise.resolve(false);
      const nextEpisodePromise = AppData!.historyService.getNextEpisode(slug);

      const [episodes, history, extras, isMyStuffAdded, nextEpisode] =
        await Promise.all([
          episodesListPromises,
          historyPromise,
          extrasPromise,
          myStuffPromise,
          nextEpisodePromise,
        ]);

      reportPageView(PageId.SERIES_DETAIL, slug);

      page.pageData = {
        show,
        episodes,
        extras,
        season,
        listItemContext: listItemContext,
        fromPageId: fromPageId ?? PageId.UNKNOWN,
        isMyStuffAdded,
        history,
        nextEpisode,
      };
    },
  },
  {
    path: `${RoutePath.LIVE_EVENT_DETAIL}/:slug`,
    component: LiveEventDetailsPage,
    widgets: EnabledWidgets[PageId.LIVE_EVENT_DETAIL],
    beforeNavigate: async (from: string | undefined, to: Request) => {
      // Set 'played' to true to ensure we don't re-navigate to playback
      const played = !!from?.startsWith(RoutePath.PLAY);
      to.register.set('played', played);

      return true;
    },
    on: async (
      page: LiveEventDetailsPage,
      routes: {
        slug: string;
        played: boolean; // Set in beforeNavigate hook
        selectItemContext: SelectItemContext | undefined;
      },
    ) => {
      const { slug, selectItemContext, played } = routes;

      // Data from the initial visit, only used when backing out of the player
      const cached =
        played && page.pageData?.liveEvent.slug === slug
          ? page.pageData
          : undefined;

      // TODO: Currently, the CW wants us to show all live events in Live Event Detail's
      // "More Live Events" row. We will eventually have to use a custom endpoint to get
      // these additional live events
      // Fall back to cached data if the fetch failed or no longer has the event
      const allLiveEvents =
        (await getContentWrapper(() => getAllLiveEvents(), !played)) ??
        cached?.recommendedLiveEvents;
      const liveEvent =
        allLiveEvents?.find(liveEvent => liveEvent.slug === slug) ??
        cached?.liveEvent;

      // When exiting player - if both fetched and cached data doesn't exist,
      // show 'The event has ended' overlay with return button.
      if (played && !liveEvent) {
        Router.navigate(RoutePath.ERROR, {
          cause: ErrorCause.LIVE_ENDED_ON_EXIT_PLAYER,
        });
        return;
      }

      if (!allLiveEvents)
        return Promise.reject('Live Event Detail: Missing live events');
      if (!liveEvent)
        return Promise.reject('Live Event Detail: Missing live event');

      reportPageView(PageId.LIVE_EVENT_DETAIL, slug);

      page.pageData = {
        liveEvent,
        played,
        recommendedLiveEvents: allLiveEvents.filter(
          liveEvent => liveEvent.slug !== slug,
        ),
        selectItemContext,
      };
    },
  },
  {
    path: `${RoutePath.PLAY}/live/:slug`,
    component: LivePlaybackPage,
    widgets: EnabledWidgets[PageId.PLAYBACK],
    options: { preventStorage: true },
    on: async (
      page: LivePlaybackPage,
      routes: {
        slug: string | undefined;
        viewContext: ViewContext;
        listItemContext: ListItemContext;
        fromPageId: PageId;
      },
    ) => {
      const { slug } = routes;
      if (!slug) return Promise.reject('Playback: Missing live event');

      const mediaContent = await getContentWrapper(() => getLiveEvent(slug));
      if (!mediaContent) return Promise.reject('Playback: Missing live event');

      page.pageData = {
        mediaContent,
        viewContext: routes.viewContext ?? StaticViewContexts.DEEPLINK,
        listItemContext: routes.listItemContext,
        fromPageId: routes.fromPageId ?? PageId.UNKNOWN,
      };
    },
  },
  {
    path: `${RoutePath.PLAY}/show/:guid?`,
    component: VodPlaybackPage,
    widgets: EnabledWidgets[PageId.PLAYBACK],
    options: { preventStorage: true },
    on: async (
      page: VodPlaybackPage,
      routes: {
        guid: string | undefined;
        viewContext: ViewContext;
        listItemContext: ListItemContext;
        fromPageId: PageId;
      },
    ) => {
      const { fromPageId, guid, listItemContext, viewContext } = routes;
      if (!guid) return Promise.reject('Playback: Cannot play missing show');

      const mediaContent = await getContentWrapper(() => getVideo(guid));
      if (!mediaContent)
        return Promise.reject('Playback: Cannot play missing show');

      const drmPromise = AppData?.drmService?.getDRMData(mediaContent);
      const progressPromise = AppData!.historyService.getVideoProgress(
        mediaContent.showSlug,
        guid,
      );
      const upNextPromise = getContentWrapper(
        async () => {
          const recommendedList = await getEndCardRecommended(guid);
          // pull the first 3. the first is the same as nextEpisode
          return recommendedList.splice(0, 3);
        },
        false,
        false,
      );

      const [drmData, progress, upNext] = await Promise.all([
        drmPromise,
        progressPromise,
        upNextPromise,
      ]);

      const hasDrm = !!drmData?.hasDrm;
      const drmLicenseUrl = drmData?.licenseUrl;
      const token = drmData?.token;

      const contentSourceId = AppData?.google.contentSourceId;
      if (!contentSourceId)
        return Promise.reject('Playback: Missing content source ID');

      page.pageData = {
        mediaContent,
        hasDrm,
        drmLicenseUrl,
        token,
        progress,
        upNext,
        viewContext: viewContext ?? StaticViewContexts.DEEPLINK,
        listItemContext: listItemContext,
        fromPageId: fromPageId ?? PageId.UNKNOWN,
      };
    },
  },
  {
    path: `${RoutePath.CONTENT_HUB}/:hubSlug/:nested?`,
    component: ContentHubPage,
    widgets: EnabledWidgets[PageId.CONTENT_HUB],
    on: async (
      page: ContentHubPage,
      routes: { hubSlug: string; nested?: boolean },
    ) => {
      const { hubSlug } = routes;
      const swimlanes = await getContentWrapper(() => getContentHub(hubSlug));
      if (!swimlanes) return Promise.reject('ContentHub: Missing swimlanes');

      // TODO EL-739: Remove workaround when swimlanes always provide title value
      if (!swimlanes.title) {
        const navItem = AppData?.navigationItems?.find(
          item => item.slug === hubSlug,
        );
        swimlanes.title = navItem?.title ?? '';
      }

      reportPageView(
        hubSlug === ContentHubSlugs.HOME ? PageId.HOME : PageId.CONTENT_HUB,
        swimlanes.title,
      );

      page.pageData = {
        swimlanes,
        template: swimlanes.layout,
        backgroundImage:
          swimlanes.backgroundImages.hubBackgroundHorizontal ?? '',
        backgroundColor: swimlanes.backgroundColor,
      };
    },
  },
  {
    path: RoutePath.MY_STUFF,
    component: MyStuffPage,
    widgets: EnabledWidgets[PageId.MY_STUFF],
    on: async (page: MyStuffPage) => {
      const isLoggedIn = !!AppData?.loginRadiusService?.isLoggedIn();

      const trendingPromise = getTrending().then(result => {
        const trendingIds = result.slice(0, CONTINUE_WATCHING_MAX_ITEMS);
        return getMediaDataFromIdentifiers(trendingIds);
      });
      const continuePromise = AppData?.historyService.getContinueLaneItems(
        CONTINUE_WATCHING_MAX_ITEMS,
      );
      const watchlistPromise = isLoggedIn
        ? getWatchlistLaneItems(WATCHLIST_MAX_ITEMS)
        : Promise.resolve(undefined);
      const channelsPromise = isLoggedIn
        ? getFavoriteChannelLaneItems(FAVORITE_CHANNELS_MAX_ITEMS)
        : Promise.resolve(undefined);

      const [
        trendingItems,
        continueLaneItems,
        watchlistLaneItems,
        favoriteChannelLaneItems,
      ] = await Promise.all([
        trendingPromise,
        continuePromise,
        watchlistPromise,
        channelsPromise,
      ]);

      page.pageData = {
        trendingItems,
        continueLaneItems,
        watchlistLaneItems,
        favoriteChannelLaneItems,
      };
    },
  },
  {
    path: RoutePath.FAVORITE_CHANNELS,
    component: MyChannelsPage,
    widgets: EnabledWidgets[PageId.FAVORITE_CHANNELS],
    on: async (page: MyChannelsPage) => {
      const favoriteChannelLaneItems = await getFavoriteChannelLaneItems();
      page.pageData = {
        favoriteChannelLaneItems,
      };
    },
  },
  {
    path: RoutePath.WATCHLIST,
    component: MyWatchlistPage,
    widgets: EnabledWidgets[PageId.WATCHLIST],
    on: async (page: MyWatchlistPage) => {
      const watchlistLaneItems = await getWatchlistLaneItems();
      page.pageData = {
        watchlistLaneItems,
      };
    },
  },
  {
    path: RoutePath.CONTINUE_WATCHING,
    component: MyHistoryPage,
    widgets: EnabledWidgets[PageId.CONTINUE_WATCHING],
    on: async (page: MyHistoryPage) => {
      const continueWatchingPromise =
        AppData?.historyService.getContinueLaneItems();
      const trendingPromise = getTrending().then(id =>
        getMediaDataFromIdentifiers(id),
      );

      const [continueWatchingItems, trendingItems] = await Promise.all([
        continueWatchingPromise,
        trendingPromise,
      ]);

      page.pageData = {
        continueWatchingItems: continueWatchingItems ?? [],
        trendingItems: trendingItems ?? [],
      };
    },
  },
  {
    path: RoutePath.ERROR,
    component: ErrorPage,
    widgets: ['ErrorModal'], // Do not change, messes with the script below
    options: { preventStorage: true },
  },
  {
    path: RoutePath.NOT_FOUND,
    beforeNavigate: async (_fromHash: string, toRequest: Request) => {
      const failedHash = toRequest.register.get('failedHash');
      reportError({
        error: new Error(`Unable to navigate to: ${failedHash}`),
        userFacing: false,
        code: ErrorCode.c47,
      });
      return true;
    },
    hook: () => navigateHome(),
  },
];

// Store the previous hash to detect same-page reloads in afterEachRoute
let previousRouteHash: string | undefined;

export default {
  boot: async () => {
    // Reset session any time the app restarts
    AppData?.storageService.clearSession();

    if (AppData?.useCacheBackup) {
      // Set up cache backup when we want to use internal cache for testing
      await useCacheBackup();
    }

    window.analytics = {} as any;

    const analyticsInitPromise = (async () => {
      // TODO: Check brand's supporting analytic services before initializing.
      // Should also have graceful handling in case of outage

      window.backgroundingService = new BackgroundingService();
      window.collectionVisibilityService = new CollectionVisibilityService();

      const loadDai = loadImaDaiScript();

      await AppData!.device.load.bind(AppData!.device)();

      window.analytics.mParticle = await MParticleAnalytics.AsyncConstruct({
        deviceIntegration: AppData!.device,
        deviceInfo: await AppData!.device.getDeviceInfo(),
      });
      AppData?.brazeService?.init(window.analytics.mParticle.braze);

      window.analytics.conviva = await ConvivaAnalytics.AsyncConstruct({
        deviceIntegration: AppData!.device,
        storageService: AppData!.storageService,
        mParticle: window.analytics.mParticle,
        loginRadiusService: AppData!.loginRadiusService,
      });
      await loadDai;

      window.analytics.comscore = new ComscoreAnalytics({
        privacyService: AppData!.privacyService,
      });
      window.analytics.nielsen = new NielsenAnalytics({
        deviceIntegration: AppData!.device,
        privacyService: AppData!.privacyService,
      });
      window.analytics.brightcove = new BrightcoveAnalytics({
        deviceIntegration: AppData!.device,
      });

      AppData!.privacyService.initPrivacyStatus({
        comscore: window.analytics.comscore,
        mParticle: window.analytics.mParticle,
        nielsen: window.analytics.nielsen,
      });
    })();

    window.analytics.initializationPromise = analyticsInitPromise;

    const migrationService = AppData!.migrationService;

    const shouldMigrateFromPreviousApp =
      migrationService.shouldMigratePreviousAppStorage();
    const migratePreviousAppStorageFn =
      migrationService.migratePreviousAppStorage.bind(migrationService);

    const shouldMigrateFromPreviousVersion =
      migrationService.shouldMigratePreviousVersionStorage();
    const migratePreviousVersionStorageFn =
      migrationService.migratePreviousVersionStorage.bind(migrationService);

    const historyService = AppData!.historyService;
    const shouldUpdateHistoryOnBoot =
      historyService.shouldUpdateLocalHistoryOnBoot();
    const updateHistoryOnBootFn =
      historyService.updateLocalHistoryOnBoot.bind(historyService);

    // Array of requests that need to be completed before the splash page finishes.
    // Note that we don't have to explicitly pass the results to the next page since
    // results get cached.
    const launchFunctions: (() => Promise<unknown>)[] = [];
    launchFunctions.push(initializeCacheTimeouts);

    if (shouldMigrateFromPreviousApp)
      launchFunctions.push(migratePreviousAppStorageFn);
    if (shouldMigrateFromPreviousVersion)
      launchFunctions.push(migratePreviousVersionStorageFn);
    if (shouldUpdateHistoryOnBoot) launchFunctions.push(updateHistoryOnBootFn);

    const loginSetup = async () => {
      const loginRadiusService = AppData?.loginRadiusService;
      const personaService = AppData?.personaService;

      try {
        if (loginRadiusService && personaService) {
          loginRadiusService.init(
            window.analytics.mParticle,
            window.analytics.conviva,
            personaService,
            AppData!.isProduction,
          );
          personaService.init(loginRadiusService);
          AppData?.smartLoginService?.init(window.backgroundingService);

          if (loginRadiusService.hasAccessToken()) {
            await loginRadiusService.autoLogin();
          }
        }
      } finally {
        // After autoLogin so the first startSDK already carries the user's
        // cross-device identity. Never rejects, so the finally can't mask errors
        await AppData!.oneTrustService?.init(window.backgroundingService);

        // Waits until One Trust is finished before moving on to the home page,
        // ensuring no tracking calls are made accidentally
        await AppData!.oneTrustService?.showBannerIfRequired();
      }

      // Check if theres a mParticle logout sync issue
      // this could happen if mParticles logout call fails (this is harder to fix then login, since auto login on launch)
      if (
        window.analytics.mParticle.isMParticleLoggedIn() &&
        !AppData?.loginRadiusService?.isLoggedIn()
      ) {
        await window.analytics.mParticle.logout();
      }

      AppData!.blueconicService?.init();
    };
    const adobePassSetup = async () => {
      return AppData?.adobePassService?.init();
    };

    launchFunctions.push(loginSetup);
    launchFunctions.push(adobePassSetup);

    SplashPage.launchFunctions = launchFunctions;
  },
  root: `${RoutePath.CONTENT_HUB}/${ContentHubSlugs.HOME}`,
  beforeEachRoute: async (fromHash: string | undefined, toRequest: Request) => {
    AppData?.listItemPromiseService.clear();

    // Store fromHash for comparison in afterEachRoute
    previousRouteHash = fromHash;
    try {
      AppData?.modalService.closeAll();
      AppData?.notificationService.resetBlockingElements();

      const pageId: string = getPageIdFromHash(toRequest.hash);

      // The analytics object is not available on the root (splash) page
      const analytics = window.analytics;
      if (analytics && pageId !== PageId.SPLASH) {
        await analytics.initializationPromise;

        if (
          // Handle these pages separately
          pageId !== PageId.CONTENT_HUB &&
          pageId !== PageId.SERIES_DETAIL &&
          pageId !== PageId.MOVIE_DETAIL &&
          pageId !== PageId.LOGIN &&
          pageId !== PageId.LIVE_EVENT_DETAIL &&
          // Exclude for playback
          pageId !== PageId.PLAYBACK
        ) {
          reportPageView(pageId);
        }
      }

      if (pageId !== PageId.SPLASH && pageId !== PageId.ERROR)
        await getConfig();
    } catch (e) {
      reportError({ error: e, userFacing: true, code: ErrorCode.c7 });

      // We can return an object with path and params for navigation
      return {
        path: RoutePath.ERROR,
        params: {
          cause: ErrorCause.CONFIGURATION,
          request: toRequest,
        },
      };
    }
    return true;
  },
  afterEachRoute: async request => {
    const [routePath] = request.url.split('/');

    // Force call to _active if navigating to the same page
    if (previousRouteHash === request.hash) {
      const activePage = Router.getActivePage();
      activePage?._active?.();
    }

    // We won't report the playback or splash page
    // Content hubs will be reported in their "on" functions
    const analytics = window.analytics;
    if (
      analytics &&
      routePath &&
      routePath !== RoutePath.PLAY &&
      routePath !== RoutePath.SPLASH &&
      routePath !== RoutePath.CONTENT_HUB
    ) {
      await analytics.initializationPromise;
    }

    if (routePath !== RoutePath.SPLASH && routePath !== RoutePath.ERROR) {
      const termsAcceptedTimestamp =
        AppData!.storageService.termsAcceptedTimestamp.get();

      const privacyModifiedDate = AppData!.privacyModifiedDate;

      const isPinRequired = AppData!.parentalPinService.isPinRequired();

      const termsAcceptedTime = new Date(termsAcceptedTimestamp ?? 0).getTime();
      const privacyModifiedTime = new Date(privacyModifiedDate).getTime();

      // If we are missing terms accepted timestamp or privacy modified date,
      const termsNotAccepted = !termsAcceptedTimestamp || !privacyModifiedDate;
      const termsExpired = termsAcceptedTime < privacyModifiedTime;

      // show app launch as safeguard
      const isOneTrustActive = !!AppData!.oneTrustService?.isInitialized;
      if (!isOneTrustActive && (termsNotAccepted || termsExpired)) {
        AppData?.modalService.open('AppLaunch');

        if (isPinRequired) {
          AppData?.modalService.modalAfter('EnterParentalPin');
        }

        return;
      }

      if (isPinRequired) {
        AppData?.modalService.open('EnterParentalPin');
      }
    }

    if (shouldShowLogin()) {
      Router.navigate(RoutePath.LOGIN);
    }
  },
  routes,
} as Router.Config;
